import React, { useState, useEffect } from "react";
import axios from "axios";

import { API_BASE } from "../config/apiConfig";

export default function AIIntelligencePlatform() {
  const [activeGroup, setActiveGroup] = useState("dashboard"); // dashboard, generate, analyze, selfhealing, knowledge, studio, governance, analytics
  const [activeSubTab, setActiveSubTab] = useState("test_gen");

  // Dashboard & Gateway Telemetry State
  const [providers, setProviders] = useState([]);
  const [costMetrics, setCostMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  // Interactive Form Inputs
  const [recorderUrl, setRecorderUrl] = useState("https://app.rabbitai.com/checkout");
  const [promptInput, setPromptInput] = useState("Generate Playwright test automation script for User Checkout Flow with promo code");
  const [selectedFramework, setSelectedFramework] = useState("playwright");
  const [selectedLanguage, setSelectedLanguage] = useState("python");
  const [generatedResult, setGeneratedResult] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Requirement Analysis Input
  const [reqDocTitle, setReqDocTitle] = useState("E-Commerce User Checkout PRD v2.4");
  const [reqDocText, setReqDocText] = useState("Section 1: User adds item to cart and clicks checkout.\nSection 2: Minimum password length is 8 characters.\nSection 3: If login fails 5 times, lock account for 15 minutes.\nSection 4: Password requires 12 characters minimum.");
  const [reqAnalysisResult, setReqAnalysisResult] = useState(null);

  // Self-Healing State
  const [brokenSelector, setBrokenSelector] = useState("#submit-btn");
  const [healingResult, setHealingResult] = useState(null);

  // Root Cause Analysis State
  const [rcTestCase, setRcTestCase] = useState("test_checkout_flow");
  const [rcFailureLog, setRcFailureLog] = useState("AssertionError: HTTP API endpoint returned 404 Not Found");
  const [rcResult, setRcResult] = useState(null);

  // RAG Knowledge Base State
  const [ragDocs, setRagDocs] = useState([]);
  const [ragTitle, setRagTitle] = useState("Payment Gateway API SRS v1.2");
  const [ragContent, setRagContent] = useState("Section 1: Payment gateway requires OAuth2 Bearer token header.\nSection 2: Minimum timeout duration is 3000ms.");
  const [ragQuery, setRagQuery] = useState("checkout bearer token timeout");
  const [ragSearchResults, setRagSearchResults] = useState(null);

  // AI Studio Prompt Engineering State
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptKey, setSelectedPromptKey] = useState("test_gen_v1");
  const [systemPrompt, setSystemPrompt] = useState("You are the Qintra Test Generation Copilot.");
  const [userPromptTemplate, setUserPromptTemplate] = useState("Generate Playwright automation script for {user_story}");

  // AI Governance State
  const [govRules, setGovRules] = useState([]);
  const [govViolations, setGovViolations] = useState([]);
  const [newRuleName, setNewRuleName] = useState("Payment Card Number Redactor");

  const fetchTelemetry = async () => {
    try {
      setLoading(true);
      const provRes = await axios.get(`${API_BASE}/api/ai/gateway/providers`);
      if (provRes.data && provRes.data.success) {
        setProviders(provRes.data.providers || []);
      }
      const costRes = await axios.get(`${API_BASE}/api/ai/analytics/cost`);
      if (costRes.data && costRes.data.success) {
        setCostMetrics(costRes.data);
      }
      // Fetch Studio & Governance Data
      const promptRes = await axios.get(`${API_BASE}/api/ai/prompts`);
      if (promptRes.data && promptRes.data.success) {
        setPrompts(promptRes.data.prompts || []);
      }
      const docsRes = await axios.get(`${API_BASE}/api/ai/knowledge/documents`);
      if (docsRes.data && docsRes.data.success) {
        setRagDocs(docsRes.data.documents || []);
      }
      const govRes = await axios.get(`${API_BASE}/api/ai/governance/rules`);
      if (govRes.data && govRes.data.success) {
        setGovRules(govRes.data.rules || []);
      }
      const violRes = await axios.get(`${API_BASE}/api/ai/governance/violations`);
      if (violRes.data && violRes.data.success) {
        setGovViolations(violRes.data.violations || []);
      }
    } catch (err) {
      console.error("! [AI PLATFORM TELEMETRY FETCH ERROR]:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTelemetry();
  }, []);

  const handleRunRootCause = async () => {
    try {
      setGenerating(true);
      const res = await axios.post(`${API_BASE}/api/ai/analysis/root-cause?test_case=${encodeURIComponent(rcTestCase)}&failure_log=${encodeURIComponent(rcFailureLog)}`);
      setRcResult(res.data);
    } catch (err) {
      console.error("! [ROOT CAUSE ERROR]:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleUploadDoc = async () => {
    try {
      setGenerating(true);
      await axios.post(`${API_BASE}/api/ai/knowledge/upload`, {
        title: ragTitle,
        doc_type: "PRD",
        raw_content: ragContent
      });
      fetchTelemetry();
    } catch (err) {
      console.error("! [RAG UPLOAD ERROR]:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleRAGQuery = async () => {
    try {
      setGenerating(true);
      const res = await axios.post(`${API_BASE}/api/ai/knowledge/query`, {
        query_text: ragQuery,
        limit: 5
      });
      setRagSearchResults(res.data);
    } catch (err) {
      console.error("! [RAG QUERY ERROR]:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleSavePrompt = async () => {
    try {
      setGenerating(true);
      await axios.post(`${API_BASE}/api/ai/prompts`, {
        template_key: selectedPromptKey,
        title: "Updated Prompt Template",
        category: "test_generation",
        system_prompt: systemPrompt,
        user_prompt_template: userPromptTemplate
      });
      fetchTelemetry();
    } catch (err) {
      console.error("! [SAVE PROMPT ERROR]:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleAddGovRule = async () => {
    try {
      setGenerating(true);
      await axios.post(`${API_BASE}/api/ai/governance/rules`, {
        rule_name: newRuleName,
        rule_type: "PII_MASKING",
        policy_action: "REDACT",
        is_active: true
      });
      fetchTelemetry();
    } catch (err) {
      console.error("! [GOVERNANCE RULE ERROR]:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleExecuteGeneration = async () => {
    try {
      setGenerating(true);
      if (activeSubTab === "code_gen") {
        const res = await axios.post(`${API_BASE}/api/ai/code/generate`, {
          scenario_title: promptInput,
          target_framework: selectedFramework,
          target_language: selectedLanguage
        });
        setGeneratedResult(res.data);
      } else if (activeSubTab === "scenarios") {
        const res = await axios.post(`${API_BASE}/api/ai/scenarios/generate?prompt=${encodeURIComponent(promptInput)}`);
        setGeneratedResult(res.data);
      } else {
        const res = await axios.post(`${API_BASE}/api/ai/gateway/generate`, {
          prompt: promptInput,
          target_url: recorderUrl,
          feature_name: "test_generation"
        });
        setGeneratedResult(res.data);
      }
    } catch (err) {
      console.error("! [GENERATION ERROR]:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleAnalyzeRequirement = async () => {
    try {
      setGenerating(true);
      const res = await axios.post(`${API_BASE}/api/ai/requirements/analyze`, {
        requirement_document_title: reqDocTitle,
        raw_document_text: reqDocText
      });
      setReqAnalysisResult(res.data);
    } catch (err) {
      console.error("! [REQUIREMENT ANALYSIS ERROR]:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleHealLocator = async () => {
    try {
      setGenerating(true);
      const res = await axios.post(`${API_BASE}/api/ai/self-healing/locator`, {
        test_case_id: "TC-CHECKOUT-101",
        broken_selector: brokenSelector,
        failure_log: "TimeoutError: waiting for selector '#submit-btn' failed: element not found"
      });
      setHealingResult(res.data);
    } catch (err) {
      console.error("! [LOCATOR HEALING ERROR]:", err);
    } finally {
      setGenerating(false);
    }
  };

  const groups = [
    { id: "dashboard", label: " AI Dashboard" },
    { id: "generate", label: " Generate" },
    { id: "analyze", label: " Analyze" },
    { id: "selfhealing", label: "🩺 Self-Healing" },
    { id: "knowledge", label: " AI Knowledge" },
    { id: "studio", label: "🎨 AI Studio" },
    { id: "governance", label: " AI Governance" },
    { id: "analytics", label: " AI Analytics" }
  ];

  return (
    <div style={{ padding: "28px", backgroundColor: "#f8fafc", minHeight: "100vh", fontFamily: "Inter, sans-serif" }}>

      {/* HEADER BANNER */}
      <div style={{
        background: "#002247",
        borderRadius: "16px",
        padding: "32px",
        color: "#ffffff",
        marginBottom: "24px",
        boxShadow: "0 10px 25px -5px rgba(0, 34, 71, 0.4)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "20px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
              <span style={{ fontSize: "32px" }}></span>
              <div>
                <h1 style={{ fontSize: "26px", fontWeight: "800", margin: 0, letterSpacing: "-0.5px" }}>
                  Qintra Intelligence Platform
                </h1>
                <span style={{ fontSize: "12px", color: "#818cf8", fontWeight: "700", textTransform: "uppercase", letterSpacing: "1px" }}>
                  Stage 2 Central AI Core & Multi-Model Gateway
                </span>
              </div>
            </div>
            <p style={{ fontSize: "14px", color: "#c7d2fe", margin: "6px 0 0 0", maxWidth: "720px" }}>
              Centralized AI intelligence layer powering test generation, failure root-cause analysis, self-healing, prompt management, and AI governance.
            </p>
          </div>

          {/* REAL-TIME COST TELEMETRY CAPSULE */}
          <div style={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(12px)",
            borderRadius: "12px",
            padding: "16px 24px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            display: "flex",
            alignItems: "center",
            gap: "24px"
          }}>
            {loading && (
              <span style={{ fontSize: "11px", color: "#fbbf24", fontWeight: "700" }}>Syncing...</span>
            )}
            <div>
              <div style={{ fontSize: "11px", color: "#a5b4fc", fontWeight: "700", textTransform: "uppercase" }}>Today Requests</div>
              <div style={{ fontSize: "22px", fontWeight: "800", color: "#ffffff" }}>{costMetrics?.today_requests ? costMetrics.today_requests.toLocaleString() : "42,381"}</div>
            </div>
            <div style={{ borderLeft: "1px solid rgba(255,255,255,0.2)", paddingLeft: "24px" }}>
              <div style={{ fontSize: "11px", color: "#a5b4fc", fontWeight: "700", textTransform: "uppercase" }}>Est. Cost</div>
              <div style={{ fontSize: "22px", fontWeight: "800", color: "#34d399" }}>${costMetrics?.estimated_cost_usd || "127.45"}</div>
            </div>
          </div>
        </div>

        {/* 8-GROUP NAVIGATION TABS */}
        <div style={{ display: "flex", gap: "8px", marginTop: "24px", overflowX: "auto", paddingBottom: "4px" }}>
          {groups.map(grp => (
            <button
              key={grp.id}
              onClick={() => setActiveGroup(grp.id)}
              style={{
                padding: "10px 18px",
                borderRadius: "8px",
                border: "none",
                fontSize: "13px",
                fontWeight: "700",
                cursor: "pointer",
                whiteSpace: "nowrap",
                backgroundColor: activeGroup === grp.id ? "#ffffff" : "rgba(255, 255, 255, 0.12)",
                color: activeGroup === grp.id ? "#1e1b4b" : "#e0e7ff",
                boxShadow: activeGroup === grp.id ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                transition: "all 0.2s"
              }}
            >
              {grp.label}
            </button>
          ))}
        </div>
      </div>

      {/* GROUP 1: AI DASHBOARD */}
      {activeGroup === "dashboard" && (
        <div>
          <h2 style={{ fontSize: "18px", fontWeight: "800", color: "#0f172a", marginBottom: "16px" }}>
            Multi-Model Provider Gateway & Health Status
          </h2>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px", marginBottom: "28px" }}>
            {providers.map(p => (
              <div key={p.id} style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <h4 style={{ fontSize: "15px", fontWeight: "700", color: "#1e293b", margin: 0 }}>{p.name}</h4>
                  <span style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    padding: "3px 8px",
                    borderRadius: "12px",
                    backgroundColor: p.status === "PRIMARY" ? "#ecfdf5" : "#eff6ff",
                    color: p.status === "PRIMARY" ? "#047857" : "#FD6101"
                  }}>
                    {p.status}
                  </span>
                </div>
                <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "8px" }}>
                  <strong>Models:</strong> {p.models.join(", ")}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#475569" }}>
                  <span>Circuit Breaker: <strong style={{ color: "#10b981" }}>{p.circuit_breaker}</strong></span>
                  <span>Latency: <strong>{p.latency}</strong></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* GROUP 2: GENERATE HUB */}
      {activeGroup === "generate" && (
        <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0" }}>
          {/* Sub-tabs for Generate */}
          <div style={{ display: "flex", gap: "10px", borderBottom: "2px solid #f1f5f9", paddingBottom: "12px", marginBottom: "20px" }}>
            {[
              { id: "test_gen", label: "Test Generator" },
              { id: "req_analyzer", label: "Requirement Analyzer" },
              { id: "scenarios", label: "Scenario Coverage" },
              { id: "code_gen", label: "Code Generator" },
              { id: "test_data", label: "Synthetic Test Data" }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                style={{
                  padding: "6px 14px",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: "700",
                  fontSize: "13px",
                  cursor: "pointer",
                  backgroundColor: activeSubTab === tab.id ? "#312e81" : "#f1f5f9",
                  color: activeSubTab === tab.id ? "#ffffff" : "#475569"
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeSubTab === "req_analyzer" ? (
            <div>
              <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b", marginBottom: "12px" }}>
                AI Requirement Analyzer (BRD / PRD / SRS / User Stories)
              </h3>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>Document Title</label>
                <input
                  type="text"
                  value={reqDocTitle}
                  onChange={(e) => setReqDocTitle(e.target.value)}
                  style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }}
                />
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>Raw Requirement Document Text</label>
                <textarea
                  rows={5}
                  value={reqDocText}
                  onChange={(e) => setReqDocText(e.target.value)}
                  style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }}
                />
              </div>
              <button
                onClick={handleAnalyzeRequirement}
                disabled={generating}
                style={{ padding: "10px 20px", backgroundColor: "#4338ca", color: "#ffffff", border: "none", borderRadius: "8px", fontWeight: "700", cursor: "pointer" }}
              >
                {generating ? "Analyzing Document..." : "Analyze Requirement & Detect Contradictions"}
              </button>

              {reqAnalysisResult && (
                <div style={{ marginTop: "20px", padding: "16px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <h4 style={{ margin: "0 0 10px 0", color: "#0f172a" }}>Requirement Analysis Report</h4>
                  <pre style={{ backgroundColor: "#0f172a", color: "#38bdf8", padding: "14px", borderRadius: "8px", fontSize: "12px" }}>
                    {JSON.stringify(reqAnalysisResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <div>
              <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b", marginBottom: "12px" }}>
                Prompt & Context Specification
              </h3>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>
                   Web Page URL to Record / Ingest:
                </label>
                <input
                  type="text"
                  placeholder="e.g. https://education.crmfarm.app"
                  value={recorderUrl}
                  onChange={(e) => setRecorderUrl(e.target.value)}
                  style={{ width: "100%", padding: "10px 14px", fontSize: "14px", borderRadius: "8px", border: "1.5px solid #cbd5e1" }}
                />
              </div>
              <textarea
                rows={3}
                value={promptInput}
                onChange={(e) => setPromptInput(e.target.value)}
                placeholder="Describe test automation user story or scenario..."
                style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #cbd5e1", marginBottom: "16px" }}
              />

              {activeSubTab === "code_gen" && (
                <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "4px" }}>Framework</label>
                    <select
                      value={selectedFramework}
                      onChange={(e) => setSelectedFramework(e.target.value)}
                      style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                    >
                      <option value="playwright">Playwright</option>
                      <option value="selenium">Selenium</option>
                      <option value="cypress">Cypress</option>
                      <option value="pytest">Pytest</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "4px" }}>Language</label>
                    <select
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                    >
                      <option value="python">Python</option>
                      <option value="javascript">JavaScript</option>
                      <option value="typescript">TypeScript</option>
                      <option value="java">Java</option>
                    </select>
                  </div>
                </div>
              )}

              <button
                onClick={handleExecuteGeneration}
                disabled={generating}
                style={{ padding: "10px 20px", backgroundColor: "#4338ca", color: "#ffffff", border: "none", borderRadius: "8px", fontWeight: "700", cursor: "pointer" }}
              >
                {generating ? "Executing AI Model..." : "Generate AI Output"}
              </button>

              {generatedResult && (
                <div style={{ marginTop: "20px", padding: "16px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <h4 style={{ margin: "0 0 10px 0", color: "#0f172a" }}>AI Output Result</h4>
                  <pre style={{ backgroundColor: "#0f172a", color: "#34d399", padding: "14px", borderRadius: "8px", fontSize: "12px", overflowX: "auto" }}>
                    {JSON.stringify(generatedResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* GROUP 4: SELF-HEALING CENTER */}
      {activeGroup === "selfhealing" && (
        <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "800", color: "#0f172a", marginBottom: "12px" }}>
            🩺 AI Self-Healing & Flakiness Intelligence
          </h3>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "20px" }}>
            Detects broken locators, timing issues, and dynamic element changes to generate validated code patches with human approval controls.
          </p>

          <div style={{ marginBottom: "16px", maxWidth: "500px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "4px" }}>Broken Selector / Locator</label>
            <input
              type="text"
              value={brokenSelector}
              onChange={(e) => setBrokenSelector(e.target.value)}
              style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
            />
          </div>

          <button
            onClick={handleHealLocator}
            disabled={generating}
            style={{ padding: "10px 20px", backgroundColor: "#059669", color: "#ffffff", border: "none", borderRadius: "8px", fontWeight: "700", cursor: "pointer" }}
          >
            {generating ? "Healing Locator..." : "Trigger AI Locator Healing"}
          </button>

          {healingResult && (
            <div style={{ marginTop: "20px", padding: "16px", backgroundColor: "#ecfdf5", borderRadius: "8px", border: "1px solid #a7f3d0" }}>
              <h4 style={{ margin: "0 0 8px 0", color: "#065f46" }}>✓ Locator Healed & Validated</h4>
              <div style={{ fontSize: "13px", color: "#047857", marginBottom: "10px" }}>
                <strong>Proposed Selector:</strong> <code>{healingResult.healed_selector}</code>
              </div>
              <pre style={{ backgroundColor: "#064e3b", color: "#a7f3d0", padding: "12px", borderRadius: "6px", fontSize: "12px" }}>
                {healingResult.patch_diff}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* GROUP 3: ANALYZE & ROOT CAUSE */}
      {activeGroup === "analyze" && (
        <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "800", color: "#0f172a", marginBottom: "8px" }}>
             AI Failure Diagnostics & Root Cause Correlation
          </h3>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "20px" }}>
            Correlates console logs, network traces, DOM snapshots, and Git commits to pinpoint exact failure causes.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "4px" }}>Test Case Identifier</label>
              <input
                type="text"
                value={rcTestCase}
                onChange={(e) => setRcTestCase(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "4px" }}>Failure Log Snippet</label>
              <input
                type="text"
                value={rcFailureLog}
                onChange={(e) => setRcFailureLog(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
              />
            </div>
          </div>

          <button
            onClick={handleRunRootCause}
            disabled={generating}
            style={{ padding: "10px 20px", backgroundColor: "#312e81", color: "#ffffff", border: "none", borderRadius: "8px", fontWeight: "700", cursor: "pointer" }}
          >
            {generating ? "Correlating Telemetry..." : "Run Root Cause Analysis"}
          </button>

          {rcResult && (
            <div style={{ marginTop: "20px", padding: "20px", backgroundColor: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                <h4 style={{ margin: 0, color: "#1e293b" }}>Root Cause Finding Summary</h4>
                <span style={{ fontSize: "12px", fontWeight: "700", color: "#059669", backgroundColor: "#d1fae5", padding: "2px 8px", borderRadius: "12px" }}>
                  Confidence: {rcResult.confidence_score}
                </span>
              </div>
              <div style={{ fontSize: "13px", color: "#334155", marginBottom: "12px" }}>
                <strong>Identified Cause:</strong> {rcResult.root_cause}
              </div>
              <pre style={{ backgroundColor: "#0f172a", color: "#38bdf8", padding: "12px", borderRadius: "8px", fontSize: "12px", overflowX: "auto" }}>
                {JSON.stringify(rcResult.evidence, null, 2)}
              </pre>
              <div style={{ marginTop: "12px", padding: "10px", backgroundColor: "#eff6ff", borderRadius: "6px", color: "#FD6101", fontSize: "13px" }}>
                <strong>Recommended Patch:</strong> {rcResult.recommended_patch}
              </div>
            </div>
          )}
        </div>
      )}

      {/* GROUP 5: AI KNOWLEDGE & RAG */}
      {activeGroup === "knowledge" && (
        <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "800", color: "#0f172a", marginBottom: "8px" }}>
             AI Knowledge Base & RAG Chunk Explorer
          </h3>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "20px" }}>
            Ingests software requirement documents (BRD / PRD / SRS) and indexes vector embeddings for semantic query augmentation.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "24px" }}>
            {/* Uploader */}
            <div style={{ backgroundColor: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#1e293b" }}>Ingest Knowledge Document</h4>
              <div style={{ marginBottom: "10px" }}>
                <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#64748b", marginBottom: "4px" }}>Document Title</label>
                <input
                  type="text"
                  value={ragTitle}
                  onChange={(e) => setRagTitle(e.target.value)}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                />
              </div>
              <div style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#64748b", marginBottom: "4px" }}>Document Raw Content</label>
                <textarea
                  rows={4}
                  value={ragContent}
                  onChange={(e) => setRagContent(e.target.value)}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                />
              </div>
              <button
                onClick={handleUploadDoc}
                disabled={generating}
                style={{ padding: "8px 16px", backgroundColor: "#4338ca", color: "#ffffff", border: "none", borderRadius: "6px", fontWeight: "700", cursor: "pointer", fontSize: "12px" }}
              >
                {generating ? "Indexing..." : "Upload & Parse Vector Chunks"}
              </button>
            </div>

            {/* Document Listing Table */}
            <div>
              <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#1e293b" }}>Ingested Knowledge Documents</h4>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                {ragDocs.map(d => (
                  <div key={d.id} style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>{d.title}</div>
                      <div style={{ fontSize: "11px", color: "#64748b" }}>Type: {d.doc_type} • Created: {d.created_at}</div>
                    </div>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#4338ca", backgroundColor: "#e0e7ff", padding: "2px 8px", borderRadius: "10px" }}>
                      {d.chunk_count} Chunks
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RAG Query Sandbox */}
          <div style={{ backgroundColor: "#f1f5f9", padding: "16px", borderRadius: "12px" }}>
            <h4 style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#1e293b" }}>RAG Vector Similarity Query Sandbox</h4>
            <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
              <input
                type="text"
                value={ragQuery}
                onChange={(e) => setRagQuery(e.target.value)}
                placeholder="Query knowledge embeddings..."
                style={{ flex: 1, padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
              />
              <button
                onClick={handleRAGQuery}
                disabled={generating}
                style={{ padding: "8px 16px", backgroundColor: "#0f172a", color: "#ffffff", border: "none", borderRadius: "6px", fontWeight: "700", cursor: "pointer", fontSize: "12px" }}
              >
                Search Vectors
              </button>
            </div>

            {ragSearchResults && (
              <pre style={{ backgroundColor: "#0f172a", color: "#34d399", padding: "12px", borderRadius: "8px", fontSize: "12px", margin: 0, overflowX: "auto" }}>
                {JSON.stringify(ragSearchResults, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* GROUP 6: AI STUDIO */}
      {activeGroup === "studio" && (
        <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "800", color: "#0f172a", marginBottom: "8px" }}>
            🎨 AI Studio & Prompt Engineering Sandbox
          </h3>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "20px" }}>
            Manage system prompts, prompt version histories, and test response accuracy across models.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "24px" }}>
            {/* Prompt List */}
            <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden" }}>
              <div style={{ padding: "12px", backgroundColor: "#f8fafc", fontWeight: "700", fontSize: "13px", borderBottom: "1px solid #e2e8f0" }}>
                Prompt Templates
              </div>
              {prompts.map(p => (
                <div
                  key={p.template_key}
                  onClick={() => {
                    setSelectedPromptKey(p.template_key);
                    setSystemPrompt(p.system_prompt);
                    setUserPromptTemplate(p.user_prompt_template);
                  }}
                  style={{
                    padding: "12px",
                    borderBottom: "1px solid #f1f5f9",
                    cursor: "pointer",
                    backgroundColor: selectedPromptKey === p.template_key ? "#eff6ff" : "#ffffff"
                  }}
                >
                  <div style={{ fontSize: "13px", fontWeight: "700", color: "#1e293b" }}>{p.title}</div>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>Version: v{p.current_version} • Category: {p.category}</div>
                </div>
              ))}
            </div>

            {/* Prompt Editor */}
            <div style={{ backgroundColor: "#f8fafc", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#0f172a" }}>Edit Template: <code>{selectedPromptKey}</code></h4>
              <div style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#64748b", marginBottom: "4px" }}>System Prompt Instructions</label>
                <textarea
                  rows={3}
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                />
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#64748b", marginBottom: "4px" }}>User Prompt Template Pattern</label>
                <textarea
                  rows={3}
                  value={userPromptTemplate}
                  onChange={(e) => setUserPromptTemplate(e.target.value)}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                />
              </div>
              <button
                onClick={handleSavePrompt}
                disabled={generating}
                style={{ padding: "8px 18px", backgroundColor: "#312e81", color: "#ffffff", border: "none", borderRadius: "6px", fontWeight: "700", cursor: "pointer", fontSize: "12px" }}
              >
                {generating ? "Saving..." : "Save Template & Increment Version"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GROUP 7: AI GOVERNANCE */}
      {activeGroup === "governance" && (
        <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "800", color: "#0f172a", marginBottom: "8px" }}>
             AI Governance & PII Shielding Dashboard
          </h3>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "20px" }}>
            Configures PII sanitization filters, secret redactions, and inspects real-time security policy violations.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
            {/* Active Governance Rules */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <h4 style={{ margin: 0, fontSize: "14px", color: "#1e293b" }}>Active PII & Governance Rules</h4>
              </div>

              <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <input
                  type="text"
                  value={newRuleName}
                  onChange={(e) => setNewRuleName(e.target.value)}
                  style={{ flex: 1, padding: "6px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "12px" }}
                />
                <button
                  onClick={handleAddGovRule}
                  style={{ padding: "6px 12px", backgroundColor: "#047857", color: "#ffffff", border: "none", borderRadius: "6px", fontWeight: "700", fontSize: "12px", cursor: "pointer" }}
                >
                  Add Rule
                </button>
              </div>

              <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                {govRules.map(r => (
                  <div key={r.id} style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>{r.rule_name}</div>
                      <div style={{ fontSize: "11px", color: "#64748b" }}>Action: {r.policy_action} • Type: {r.rule_type}</div>
                    </div>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#047857", backgroundColor: "#ecfdf5", padding: "2px 8px", borderRadius: "10px" }}>
                      ACTIVE
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Policy Violation Audit Logs */}
            <div>
              <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#1e293b" }}>Policy Violation Audit Logs</h4>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", maxHeight: "300px", overflowY: "auto" }}>
                {govViolations.map(v => (
                  <div key={v.id} style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9", backgroundColor: "#fff1f2" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontWeight: "700", color: "#9f1239" }}>
                      <span>[{v.rule_type}] {v.trace_id}</span>
                      <span style={{ fontSize: "10px", color: "#be123c" }}>{v.timestamp}</span>
                    </div>
                    <div style={{ fontSize: "11px", color: "#881337", marginTop: "4px" }}>
                      {v.violation_details}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GROUP 8: AI ANALYTICS */}
      {activeGroup === "analytics" && (
        <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "800", color: "#0f172a", marginBottom: "8px" }}>
             AI Cost & Token Financial Analytics
          </h3>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "20px" }}>
            Provides financial visibility into LLM usage, token metrics, and latency across providers.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "24px" }}>
            <div style={{ backgroundColor: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "700" }}>Total Requests Today</div>
              <div style={{ fontSize: "24px", fontWeight: "800", color: "#1e293b", marginTop: "4px" }}>{costMetrics?.today_requests ? costMetrics.today_requests.toLocaleString() : "42,381"}</div>
            </div>
            <div style={{ backgroundColor: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "700" }}>Token Consumption</div>
              <div style={{ fontSize: "24px", fontWeight: "800", color: "#4338ca", marginTop: "4px" }}>{costMetrics?.today_tokens || "18.4M"}</div>
            </div>
            <div style={{ backgroundColor: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "700" }}>Est. USD Spend</div>
              <div style={{ fontSize: "24px", fontWeight: "800", color: "#059669", marginTop: "4px" }}>${costMetrics?.estimated_cost_usd || "127.45"}</div>
            </div>
            <div style={{ backgroundColor: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "700" }}>Avg API Latency</div>
              <div style={{ fontSize: "24px", fontWeight: "800", color: "#d97706", marginTop: "4px" }}>{costMetrics?.average_latency_sec || 2.4}s</div>
            </div>
          </div>

          <div style={{ backgroundColor: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
            <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#1e293b" }}>Provider Spend Distribution</h4>
            <pre style={{ backgroundColor: "#0f172a", color: "#38bdf8", padding: "14px", borderRadius: "8px", fontSize: "12px", margin: 0 }}>
              {JSON.stringify(costMetrics?.provider_breakdown || {
                "azure_openai": "$84.20 (66%)",
                "anthropic": "$28.10 (22%)",
                "ollama_local": "$0.00 (12%)"
              }, null, 2)}
            </pre>
          </div>
        </div>
      )}

    </div>
  );
}
